import DriveSelector from '@/components/dialogs/DriveSelector.vue';
import LibrarySelector from '@/components/dialogs/LibrarySelector.vue';
import SceneExport from '@/components/dialogs/SceneExport.vue';
import { default as ExportProject } from '@/components/export/Projector.vue';
import { LED_SCREEN } from '@/constants/object';
import { PANEL_WIDTH, SETTING_PANEL_TABS } from '@/constants/settingPanels';
import { useAssetStore } from '@/stores/api/asset';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useAppStore } from '@/stores/appStore';
import { cameraControls, measurementHandler } from '@/threejs/core';
import { extractApiMessage, extractApiStatus } from '@/utils/apiResponse';
import eventBus from '@/utils/eventBus';
import { getGroundYOffset, getNonOverlappingPosition } from '@/utils/positionUtils';
import { useDialog, useToast } from '@disguise/cloud-ui';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { TEST_PATTERNS } from '../constants/testPattern';

export function useAppAction() {
  const cruxModuleStore = useCruxModuleStore();
  const assetStore = useAssetStore();
  const router = useRouter();
  const selectedNodeObject = ref(null);
  const dialog = useDialog();
  const toast = useToast();

  const appStore = useAppStore();

  const exit = () => {
    router.push('/');
  };
  const newFile = () => {
    router.push('/new');
  };

  const hidePanel = () => {
    if (appStore.rightSettingPanelWidth <= PANEL_WIDTH.RIGHT_MIN) {
      appStore.setRightSettingPanelWidth(PANEL_WIDTH.RIGHT_MAX);
    } else {
      appStore.setRightSettingPanelWidth(PANEL_WIDTH.RIGHT_MIN);
    }
  };

  const addSceneNode = (type, extra) => {
    const parentId = selectedNodeObject.value?.type === 'group' ? selectedNodeObject.value?.id : null;

    const allModules = cruxModuleStore.cruxModules;
    const basePosition = cameraControls._target.clone();
    const position = getNonOverlappingPosition(basePosition, allModules);

    if (type === 'group') {
      cruxModuleStore.addSceneModule(null, 'group', {
        visible: true,
        position: position,
        _isNew: true,
      });
    } else if (type === 'plane') {
      position.y = getGroundYOffset('plane');
      cruxModuleStore.addSceneModule(null, 'geometry', {
        shape: 'plane',
        width: 1,
        height: 1,
        visible: true,
        position: position,
        _isNew: true,
      });
    } else if (type === 'box') {
      position.y = getGroundYOffset('cube', { height: 1 });
      cruxModuleStore.addSceneModule(null, 'geometry', {
        shape: 'cube',
        width: 1,
        height: 1,
        depth: 1,
        visible: true,
        position: position,
        lock_scale: true,
        _isNew: true,
      });
    } else if (type === 'cylinder') {
      position.y = getGroundYOffset('cylinder');
      cruxModuleStore.addSceneModule(null, 'geometry', {
        shape: 'cylinder',
        visible: true,
        position: position,
        lock_scale: true,
        _isNew: true,
      });
    } else if (type === 'sphere') {
      position.y = getGroundYOffset('sphere');
      cruxModuleStore.addSceneModule(null, 'geometry', {
        shape: 'sphere',
        visible: true,
        position: position,
        lock_scale: true,
        _isNew: true,
      });
    } else if (type === 'torusknot') {
      position.y = getGroundYOffset('torusknot');
      cruxModuleStore.addSceneModule(null, 'geometry', {
        shape: 'torusknot',
        visible: true,
        position: position,
        lock_scale: true,
        _isNew: true,
      });
    } else if (type === 'model') {
      let name = extra.latest_version.name;
      let resourceId = extra.code;
      let ext = extra.latest_version.extension;
      position.y = getGroundYOffset('model');
      cruxModuleStore.addSceneModule(null, 'model', {
        name: name,
        resource_id: resourceId,
        ext: ext,
        visible: true,
        position: position,
        lock_scale: true,
        _isNew: true,
      });
    } else if (type === 'led') {
      const tileWidth = extra?.panel_width_mm ? Number(extra.panel_width_mm) / 1000 : LED_SCREEN.TILE_WIDTH;
      const tileHeight = extra?.panel_height_mm ? Number(extra.panel_height_mm) / 1000 : LED_SCREEN.TILE_HEIGHT;
      position.y = getGroundYOffset('led_screen', {
        tileHeight: tileHeight,
        verticalSize: LED_SCREEN.VERTICAL_SIZE,
      });
      cruxModuleStore.addSceneModule(null, 'led_screen', {
        parentId: parentId,
        libraryCode: extra?.code,
        name: extra?.name,
        _isNew: true,
        horizontalSize: LED_SCREEN.HORIZONTAL_SIZE,
        verticalSize: LED_SCREEN.VERTICAL_SIZE,
        resolutionHeight: Number(extra?.resolution_height) || LED_SCREEN.RESOLUTION_HEIGHT,
        resolutionWidth: Number(extra?.resolution_width) || LED_SCREEN.RESOLUTION_WIDTH,
        horizontalCurvature: LED_SCREEN.HORIZONTAL_CURVATURE,
        tileWidth: tileWidth,
        tileHeight: tileHeight,
        verticalCurvature: LED_SCREEN.VERTICAL_CURVATURE,
        brightness: LED_SCREEN.BRIGHTNESS,
        power: true,
        visible: true,
        inputSource: TEST_PATTERNS.CALIBRATION_GRID,
        spacing: LED_SCREEN.SPACING,
        position: position,
      });
    } else if (type === 'projector') {
      // Projectors start at origin rather than camera target so they face the scene centre by default
      const projectorPosition = getNonOverlappingPosition({ x: 0, y: 0, z: 0 }, allModules);
      cruxModuleStore.addSceneModule(null, 'projector', {
        name: extra?.name || 'Default Projector',
        libraryCode: extra?.code,
        _isNew: true,
        mesh: extra?.mesh,
        brand: extra?.brand,
        thumbnail: extra?.thumbnail,
        power: true,
        power_brightness: 100,
        visible: true,
        inputSource: TEST_PATTERNS.CALIBRATION_GRID,
        resolutionHeight: Number(extra?.resolution_height) || 1080,
        resolutionWidth: Number(extra?.resolution_width) || 1920,
        lumens: Number(extra?.lumens) || 20000,
        throwRatio: Number(extra?.throw_ratio) || 1.4,
        position: projectorPosition,
        target: { x: projectorPosition.x, y: projectorPosition.y, z: projectorPosition.z - 4 },
        rotation: { x: 0, y: 0, z: 0 },
        throwDistance: 4,
      });
    }
  };

  const openLibraryModal = (type) => {
    dialog.open(LibrarySelector, {
      data: {
        filter: type,
      },
      props: {
        header: type === 'led' ? 'LED Panel List' : 'Projector List',
        style: {
          width: '50vw',
        },
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw',
        },
        modal: true,
      },
      onClose: (options) => {
        if (options.data && options.data.buttonType !== 'Cancel') {
          addSceneNode(type, options.data);
        }
      },
    });
  };

  const addModelSceneNode = () => {
    dialog.open(DriveSelector, {
      data: {
        filter: 'scene',
      },
      props: {
        dismissableMask: true,
        header: 'Asset List',
        style: {
          width: '50vw',
        },
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw',
        },
        modal: true,
      },
      onClose: (options) => {
        const data = options.data;
        if (data && !data.buttonType) {
          addSceneNode('model', data);
        }
      },
    });
  };

  const addCameraSceneNode = () => {
    eventBus.emit('selectRightPanelTab', SETTING_PANEL_TABS.BOOKMARKS);
    cruxModuleStore.addSceneModule(assetStore.currentAsset.latest_version.code, 'camera_bookmark', {
      code: crypto.randomUUID(),
      position: {
        x: cameraControls.camera.position.x,
        y: cameraControls.camera.position.y,
        z: cameraControls.camera.position.z,
      },
      target: {
        x: cameraControls._target.x,
        y: cameraControls._target.y,
        z: cameraControls._target.z,
      },
      fov: cameraControls._zoom0 * 100,
    });
  };

  const newMeasurement = () => {
    if (measurementHandler) {
      measurementHandler.startMeasurement();
    }
  };

  const exportProjectorCsv = () => {
    dialog.open(ExportProject, {
      props: {
        header: 'Export Projector Data - CSV',
        style: {
          width: '55vw',
        },
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw',
        },
        modal: true,
      },
    });
  };

  const togglePhotometricView = () => {
    cruxModuleStore.updateModuleData('viewport_settings', 'render.view', 'photometric');

    // Switch to reports tab
    eventBus.emit('selectRightPanelTab', 'reports');
  };

  const handleApiResponse = (response) => {
    const status = extractApiStatus(response);
    let message = extractApiMessage(response);

    const successCodes = [200, 201, 204];
    const isSuccess = successCodes.includes(status);
    if (status === 500) message = 'Server error. Please try again later.';

    appStore.setLoading(false);

    if (isSuccess) {
      toast.add({
        severity: 'success',

        detail: message ?? 'Operation completed successfully.',
        life: 3000,
        group: 'br',
      });
    } else {
      toast.add({
        severity: 'error',

        detail: message ?? 'Something went wrong. Please try again.',
        life: 5000,
        group: 'br',
      });
    }
  };
  const exportScene = () => {
    dialog.open(SceneExport, {
      props: {
        header: 'Export 3D Mesh',
        style: {
          width: '500px',
        },
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw',
        },
        modal: true,
      },
    });
  };

  return {
    selectedNodeObject,
    exit,
    newFile,
    addSceneNode,
    openLibraryModal,
    addModelSceneNode,
    addCameraSceneNode,
    newMeasurement,
    hidePanel,
    exportProjectorCsv,
    togglePhotometricView,
    handleApiResponse,
    exportScene,
  };
}
